import React, { useState } from 'react'
import { Popover, Typography, Button, CircularProgress, Autocomplete, TextField, Box } from '@mui/material'
import BadgeFilter from './BadgeFilter'
import Helper from '../../helpers/Helper'
import { ISystemConfig } from '../../definitions/MiscSchema'

interface Props {
    name: string
    value: string[]
    isLoading: boolean
    options: ISystemConfig[]
    onApply: (newValues: string[]) => void
}

const SelectMultipleBadgeFilter = ({ name, isLoading, value, options, onApply }: Props) => {
    const isActive = Helper.hasVal(value) && value.length > 0

    const [selections, setSelections] = useState(isActive ? value : [])
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const isOpen = Boolean(anchorEl)
    const selectedOptions = options.filter(x => selections?.includes(x.uuid))

    const handleApply = () => {
        onApply(selections)
        setAnchorEl(null)
    }

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setSelections(isActive ? value : [])
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setSelections(value)
        setAnchorEl(null)
    }

    return (
        <div>
            <div style={{ width: 'fit-content' }} onClick={openPopover}>
                <BadgeFilter
                    isActive={isActive}
                    name={name}
                    onReset={() => {
                        setSelections([])
                    }}
                />
            </div>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        style: {
                            minWidth: 320,
                            maxWidth: 400
                        }
                    }
                }}>
                {isLoading
                    ? (
                            <Box height={100} width='100%' display='flex' justifyContent='center' alignItems='center'>
                                <CircularProgress />
                            </Box>
                        )
                    : (
                            <Box p={1} width='100%'>
                                <Typography variant='overline'>
                                    Filter by
                                    {' ' + name}
                                </Typography>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    id='tags-standard'
                                    options={options}
                                    value={selectedOptions}
                                    getOptionLabel={option => {
                                        if (!option.name) {
                                            console.error('Missing name for option:', option)
                                        }
                                        return option.name ?? option.uuid.toString()
                                    }}
                                    onChange={(e, values) => {
                                        setSelections(values.map(x => x.uuid))
                                    }}
                                    renderOption={(props, option) => (
                                        <Box component='li' {...props} key={option.uuid} sx={{ padding: 1 }}>
                                            <Typography>{option.name}</Typography>
                                        </Box>
                                    )}
                                    renderInput={params => {
                                        return (
                                            <TextField
                                                {...params}
                                                variant='standard'
                                                label='Multiple values'
                                            />
                                        )
                                    }}
                                />
                                <Button sx={{ marginTop: 2 }} fullWidth size='small' variant='outlined' onClick={handleApply}>
                                    Apply
                                </Button>
                            </Box>
                        )}
            </Popover>
        </div>
    )
}

export default SelectMultipleBadgeFilter
